import { Clock, DollarSign, Building2 } from 'lucide-react';
import type { SparkPlaybookCard } from '@sizzlenz/shared';
import { CONTENT_TYPE_CONFIG } from '@sizzlenz/shared';

interface PlaybookCardProps {
  playbook: SparkPlaybookCard;
  /** Compact mode renders a smaller card similar in size to ContentCard. */
  compact?: boolean;
}

export function PlaybookCard({ playbook, compact = false }: PlaybookCardProps) {
  const typeConfig = CONTENT_TYPE_CONFIG['playbook'];

  if (compact) {
    return (
      <div className="group relative rounded-xl border border-gray-200 bg-white p-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 h-full flex flex-col">
        <span
          className={`absolute top-2 right-2 rounded-full text-[10px] px-2 py-0.5 font-semibold shadow-sm ${typeConfig.color}`}
        >
          {typeConfig.label}
        </span>
        <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2 pr-16">
          {playbook.title}
        </h3>
        <p className="text-gray-500 text-xs leading-relaxed line-clamp-3 flex-1">
          {playbook.description}
        </p>
        {(playbook.effort || playbook.budget) && (
          <div className="flex flex-wrap gap-2 mt-3 text-[11px] text-gray-400">
            {playbook.effort && (
              <span className="inline-flex items-center gap-0.5">
                <Clock className="h-3 w-3" />
                {playbook.effort}
              </span>
            )}
            {playbook.budget && (
              <span className="inline-flex items-center gap-0.5">
                <DollarSign className="h-3 w-3" />
                {playbook.budget}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`group relative rounded-2xl border ${playbook.borderColor} bg-white p-7 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full flex flex-col`}
    >
      <span
        className={`absolute top-3 right-3 rounded-full text-xs px-3 py-1 font-semibold shadow-sm ${typeConfig.color}`}
      >
        {typeConfig.label}
      </span>
      <h3 className="text-lg font-bold text-gray-900 mb-2 pr-20">{playbook.title}</h3>
      <p className="text-gray-500 text-sm mb-5 leading-relaxed flex-1">{playbook.description}</p>

      {/* Meta */}
      <div className="flex flex-wrap gap-3 mb-4 text-xs text-gray-500">
        {playbook.effort && (
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {playbook.effort}
          </span>
        )}
        {playbook.budget && (
          <span className="inline-flex items-center gap-1">
            <DollarSign className="h-3 w-3" />
            {playbook.budget}
          </span>
        )}
        {playbook.businessTypes?.[0] && (
          <span className="inline-flex items-center gap-1">
            <Building2 className="h-3 w-3" />
            {playbook.businessTypes[0]}
          </span>
        )}
      </div>

      {playbook.outcome && (
        <div className="pt-4 border-t border-gray-100">
          <span className="text-xs font-semibold text-primary-600">{playbook.outcome}</span>
        </div>
      )}
    </div>
  );
}
